/**
 * conditions-confirm.js — Shared Use/Access Conditions review modal.
 *
 * Presents the dataset's Use Conditions (gmd:useLimitation → props.rights)
 * and Access Conditions (gmd:otherConstraints → props.otherConstraints,
 * falling back to props.accessConstraints when only the code is present)
 * to the user, and runs the supplied download action only after the user
 * acknowledges them. Used by:
 *
 *   - secims_view/data-catalog/index.html    (row Download + modal Download)
 *   - secims_view/map/js/LegendControl.js    (legend Download GeoJSON,
 *                                             coverage Download GeoTIFF,
 *                                             metadata modal Download)
 *
 * Dual-mode loading mirrors bbox-thumbnail.js: an IIFE that publishes
 * `window.SecimsConditionsConfirm` and also exports the same API via
 * module.exports so the webpack-bundled map can import it as an ES module.
 *
 * Public API (window.SecimsConditionsConfirm):
 *
 *   show(props, onAccept, opts)
 *     props    : { rights, otherConstraints, accessConstraints }
 *                Usually the `properties` block of an OGC API record.
 *     onAccept : () => void   — invoked when the user clicks Download.
 *     opts     : { title }    — optional dataset title shown in the header.
 *
 *   showForRecordUrl(url, onAccept, opts)
 *     Convenience: fetches the record JSON and then calls show(). Used by
 *     download entry points that only have a metadata URL (e.g. the
 *     per-layer download link in the map legend).
 */
(function () {
  'use strict';

  function _esc(str) {
    var d = document.createElement('div');
    d.textContent = (str == null) ? '' : String(str);
    return d.innerHTML;
  }

  function _extractConditions(props) {
    props = props || {};
    var use    = props.rights ? String(props.rights).trim() : '';
    var access = '';
    if (props.otherConstraints && String(props.otherConstraints).trim()) {
      access = String(props.otherConstraints).trim();
    } else if (props.accessConstraints && String(props.accessConstraints).trim()) {
      // Fall back to the MD_RestrictionCode value (e.g. "otherRestrictions")
      // only when no free-text access conditions are present. Better to
      // surface the code than to claim there are no conditions at all.
      access = String(props.accessConstraints).trim();
    }
    return { use: use, access: access };
  }

  function _injectStyles() {
    if (document.getElementById('secims-conditions-confirm-styles')) return;
    var style = document.createElement('style');
    style.id = 'secims-conditions-confirm-styles';
    style.textContent =
      '#secims-conditions-confirm{position:fixed;inset:0;z-index:10050;' +
      'display:flex;align-items:center;justify-content:center;}' +
      '#secims-conditions-confirm .scc-backdrop{position:absolute;inset:0;' +
      'background:rgba(0,0,0,0.45);}' +
      '#secims-conditions-confirm .scc-dialog{position:relative;' +
      'background:#fff;border-radius:6px;box-shadow:0 12px 40px rgba(0,0,0,0.25);' +
      'width:min(560px,92vw);max-height:88vh;display:flex;flex-direction:column;' +
      'font-family:"Segoe UI",Arial,sans-serif;color:#222;}' +
      '#secims-conditions-confirm .scc-header{padding:14px 18px;' +
      'border-bottom:1px solid #e2e6eb;display:flex;align-items:center;' +
      'justify-content:space-between;gap:1rem;}' +
      '#secims-conditions-confirm .scc-header h3{margin:0;font-size:1rem;' +
      'color:#003f7a;font-weight:600;}' +
      '#secims-conditions-confirm .scc-subtitle{font-size:.8rem;color:#666;' +
      'margin-top:2px;}' +
      '#secims-conditions-confirm .scc-body{padding:16px 18px;overflow:auto;}' +
      '#secims-conditions-confirm .scc-intro{font-size:.88rem;color:#444;' +
      'margin:0 0 14px 0;line-height:1.45;}' +
      '#secims-conditions-confirm .scc-field{margin-bottom:14px;}' +
      '#secims-conditions-confirm .scc-field:last-child{margin-bottom:0;}' +
      '#secims-conditions-confirm .scc-label{font-size:.72rem;font-weight:700;' +
      'text-transform:uppercase;letter-spacing:.5px;color:#003f7a;' +
      'margin-bottom:4px;}' +
      '#secims-conditions-confirm .scc-value{font-size:.92rem;line-height:1.5;' +
      'background:#f6f8fa;border:1px solid #e2e6eb;border-radius:4px;' +
      'padding:8px 10px;white-space:pre-wrap;word-break:break-word;}' +
      '#secims-conditions-confirm .scc-value.empty{color:#888;font-style:italic;}' +
      '#secims-conditions-confirm .scc-footer{padding:12px 18px;' +
      'border-top:1px solid #e2e6eb;display:flex;justify-content:flex-end;' +
      'gap:.5rem;}' +
      '#secims-conditions-confirm .scc-btn{font:inherit;font-size:.88rem;' +
      'padding:7px 14px;border-radius:4px;border:1px solid transparent;' +
      'cursor:pointer;}' +
      '#secims-conditions-confirm .scc-btn-secondary{background:#fff;' +
      'border-color:#c2c8d0;color:#333;}' +
      '#secims-conditions-confirm .scc-btn-secondary:hover{background:#f1f3f5;}' +
      '#secims-conditions-confirm .scc-btn-primary{background:#0057a8;' +
      'border-color:#0057a8;color:#fff;}' +
      '#secims-conditions-confirm .scc-btn-primary:hover{background:#004a91;}';
    document.head.appendChild(style);
  }

  function _renderValue(text) {
    if (text) {
      return '<div class="scc-value">' + _esc(text) + '</div>';
    }
    return '<div class="scc-value empty">Not specified</div>';
  }

  function show(props, onAccept, opts) {
    opts = opts || {};
    _injectStyles();

    // Remove any prior instance so back-to-back invocations don't stack.
    var existing = document.getElementById('secims-conditions-confirm');
    if (existing) existing.remove();

    var conds = _extractConditions(props);
    var title = opts.title ? String(opts.title) : '';

    var overlay = document.createElement('div');
    overlay.id = 'secims-conditions-confirm';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'scc-heading');

    var subtitle = title
      ? '<div class="scc-subtitle">' + _esc(title) + '</div>'
      : '';

    overlay.innerHTML =
      '<div class="scc-backdrop"></div>' +
      '<div class="scc-dialog">' +
        '<div class="scc-header">' +
          '<div>' +
            '<h3 id="scc-heading">Review conditions before download</h3>' +
            subtitle +
          '</div>' +
        '</div>' +
        '<div class="scc-body">' +
          '<p class="scc-intro">' +
            'The conditions below apply to this dataset. Please review them ' +
            'before downloading.' +
          '</p>' +
          '<div class="scc-field">' +
            '<div class="scc-label">Use Conditions</div>' +
            _renderValue(conds.use) +
          '</div>' +
          '<div class="scc-field">' +
            '<div class="scc-label">Access Conditions</div>' +
            _renderValue(conds.access) +
          '</div>' +
        '</div>' +
        '<div class="scc-footer">' +
          '<button type="button" class="scc-btn scc-btn-secondary" ' +
          'data-action="cancel">Cancel</button>' +
          '<button type="button" class="scc-btn scc-btn-primary" ' +
          'data-action="accept">Accept &amp; download</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    function close() {
      document.removeEventListener('keydown', onKey);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);

    overlay.querySelector('.scc-backdrop').addEventListener('click', close);
    overlay.querySelector('[data-action="cancel"]').addEventListener('click', close);
    overlay.querySelector('[data-action="accept"]').addEventListener('click', function () {
      close();
      try { onAccept && onAccept(); }
      catch (err) { console.error('conditions-confirm onAccept failed:', err); }
    });

    // Focus the primary action so keyboard users can confirm with Enter.
    var primaryBtn = overlay.querySelector('[data-action="accept"]');
    if (primaryBtn) primaryBtn.focus();
  }

  function showForRecordUrl(url, onAccept, opts) {
    if (!url) { show({}, onAccept, opts); return; }
    fetch(url)
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (data) {
        var props = (data && data.properties) || {};
        var mergedOpts = opts || {};
        if (!mergedOpts.title) {
          mergedOpts = Object.assign({}, mergedOpts,
            { title: props.title || (data && data.id) || '' });
        }
        show(props, onAccept, mergedOpts);
      })
      .catch(function (err) {
        // Network or parse failure — fall back to a no-conditions confirm
        // rather than blocking the download silently. The user still has
        // to click through, so the gate isn't bypassed.
        console.warn('conditions-confirm: failed to fetch ' + url + ':', err);
        show({}, onAccept, opts);
      });
  }

  var api = { show: show, showForRecordUrl: showForRecordUrl };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (typeof window !== 'undefined') {
    window.SecimsConditionsConfirm = api;
  }
})();
